import styled from "styled-components";
import Table from "../../common/components/Table";
import {
  UserIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { ChatSession } from "../container/ChatHistoryContainer.hook";

interface ChatHistoryTableProps {
  sessions: ChatSession[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  selectedUserId: string | null;
  formatDate: (date: string) => string;
  onPageChange: (page: number) => void;
  onUserHistoryClick: (userId: string) => void;
  onSessionDetailClick: (session: ChatSession) => void;
}

function ChatHistoryTable({
  sessions,
  isLoading,
  page,
  totalPages,
  selectedUserId,
  formatDate,
  onPageChange,
  onUserHistoryClick,
  onSessionDetailClick,
}: ChatHistoryTableProps) {
  const isEmpty = !isLoading && sessions.length === 0;

  return (
    <Table.Wrapper className="h-full">
      <Table.Header
        tabs={[
          { label: "세션 제목", className: "w-[20%]" },
          { label: "사용자 (전화번호)", className: "w-[20%]" },
          { label: "세션 생성일", className: "w-[20%]" },
          { label: "대화수", className: "w-[15%]" },
          { label: "사용자 내역", className: "w-[12.5%]" },
          { label: "세션 내용", className: "w-[12.5%]" },
        ]}
      />
      <Table.Body
        isLoading={isLoading}
        isEmpty={isEmpty}
        emptyMessage="조회된 채팅 내역이 없습니다."
      >
        {sessions.map((session) => {
          const isHighlighted = selectedUserId === session.userId;
          const shouldShow = !selectedUserId || isHighlighted;

          if (!shouldShow) return null;

          return (
            <HighlightableRow key={session.id} $highlighted={isHighlighted}>
              <Table.Column className="w-[20%]">
                <span className="truncate" title={session.title}>
                  {session.title}
                </span>
              </Table.Column>
              <Table.Column className="w-[20%]">
                <span className="truncate" title={session.userPhone}>
                  {session.userPhone}
                </span>
              </Table.Column>
              <Table.Column className="w-[20%]">
                <span className="truncate" title={formatDate(session.createdAt)}>
                  {formatDate(session.createdAt)}
                </span>
              </Table.Column>
              <Table.Column className="w-[15%]">
                <span>{session.messageCount}</span>
              </Table.Column>
              <Table.Column className="w-[12.5%]">
                <button
                  onClick={() => onUserHistoryClick(session.userId)}
                  className="flex items-center gap-1 rounded bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
                  title="사용자 내역 보기"
                >
                  <UserIcon className="h-4 w-4" />
                  <span>보기</span>
                </button>
              </Table.Column>
              <Table.Column className="w-[12.5%]">
                <button
                  onClick={() => onSessionDetailClick(session)}
                  className="flex items-center gap-1 rounded bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
                  title="세션 내용 보기"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  <span>보기</span>
                </button>
              </Table.Column>
            </HighlightableRow>
          );
        })}
      </Table.Body>
      {!isEmpty && !isLoading && totalPages > 1 && (
        <div className="border border-t-0 border-zinc-300 bg-white py-4">
          <Table.Pagination
            page={page}
            total={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Table.Wrapper>
  );
}

const HighlightableRow = styled(Table.Row)<{ $highlighted: boolean }>(
  ({ $highlighted }) => ({
    backgroundColor: $highlighted ? "#dbeafe" : "transparent",
    borderLeft: $highlighted ? "4px solid #3b82f6" : "none",
    transition: "all 0.2s ease",
  })
);

export default ChatHistoryTable;
